/**
 * exportOptimizer.js — the export pipeline's optimization stage.
 *
 *   Editor Model -> StyleResolver -> Normalization -> Optimization -> Wire serialization
 *
 * This module contains ONLY optimization decisions and wire-key computation
 * — zero cascade/default/inheritance comparison logic of its own. Every
 * "is this explicit? is it redundant?" question is answered by
 * node.resolveStyleTrace() (StyleResolver.resolveTrace under the hood), the
 * single canonical implementation of the widget/theme/part/state cascade
 * (see StyleResolver.js). The goal: emit only values that actually change
 * the effective rendered result, regardless of how many redundant explicit
 * values the editor model happens to carry.
 */

import { toWireKey } from './wireFormat.js'
import { splitColorOpa } from '../../../utils/color.js'
import { imageSourceToWireValue } from '../../imageSource.js'

/**
 * Color values can be redundant without being string-identical: a property's
 * explicit value may carry an embedded alpha suffix (hex8, e.g. main.bg_color
 * combined for the Property Editor's picker — see ConfigManager.js) that the
 * cascade level beneath it doesn't. This is a data-ENCODING equivalence
 * check, not a cascade decision — it never re-derives what the cascade
 * produced, it only asks whether two already-resolved values (both supplied
 * by StyleResolver.resolveTrace) represent the same rendered color.
 */
function colorValuesEqual(value, belowValue) {
    if (belowValue === undefined) return false
    const a = splitColorOpa(value ?? '#00000000')
    const b = splitColorOpa(belowValue ?? '#00000000')
    return a.color === b.color && Math.round(a.opacity * 255) === Math.round(b.opacity * 255)
}

/**
 * Compute the minimal set of part-scoped style wire entries needed to
 * reproduce a node's current appearance, across every semantic state the
 * node's schema supports. Skips properties marked `internal` (never
 * exported) or `exportSpecial` (handled by dedicated logic elsewhere, e.g.
 * padding's pad_top/right/left/bottom expansion in ObjectExport.js).
 *
 * @param node   any HaspObject/HaspPage — must expose resolveStyleTrace()
 * @param schema the object's v2 schema
 * @returns Array<{ partName, propName, state, meta, value, wireKey }>
 */
export function computeEffectiveStyleEntries(node, schema) {
    const entries = []
    for (const [partName, partDef] of Object.entries(schema.parts ?? {})) {
        for (const [propName, meta] of Object.entries(partDef.properties ?? {})) {
            if (meta?.internal || meta?.exportSpecial) continue
            for (const state of ['default', ...(schema.states ?? [])]) {
                const trace = node.resolveStyleTrace(partName, propName, state)
                /* `exportAlways` opts a property out of every stripping rule:
                 * it is emitted from whatever the cascade resolved, explicit or
                 * not. Only for values the firmware cannot reproduce by itself
                 * — see the flag's note in shared.js. Undefined is still
                 * skipped: there is nothing to send.
                 *
                 * Deliberately limited to the DEFAULT state. The flag exists to
                 * guarantee the baseline reaches the firmware; applying it per
                 * state would emit the same value again under every state
                 * suffix a schema declares, which changes nothing on the plate
                 * and lengthens every line. Non-default states keep the normal
                 * explicit/redundant rules, so a genuine per-state override is
                 * still exported. */
                if (meta?.exportAlways && state === 'default') {
                    if (trace.value === undefined) continue
                } else {
                    if (!trace.explicit) continue
                    if (!meta?.preserveExplicit) {
                        const redundant = meta?.type === 'color'
                            ? colorValuesEqual(trace.value, trace.belowValue)
                            : trace.redundant
                        if (redundant) continue
                    }
                }
                entries.push({
                    partName,
                    propName,
                    state,
                    meta,
                    value: trace.value,
                    wireKey: toWireKey(partName, propName, partDef, state),
                })
            }
        }
    }
    return entries
}

/**
 * Apply one effective style entry onto a flat wire-format objectData map,
 * with the same per-type handling (keep_circle-guarded radius, split colors,
 * empty-string guard) the exporter has always used — centralized here so
 * ObjectExport.js/HaspPage.js don't each hand-roll it.
 *
 * @param node        the exporting node (needs .style()/.addSplitColors())
 * @param objectData  the flat wire-format object being built for this object/page
 * @param theme       the active theme's per-object-type slice (for addSplitColors' default-color check)
 * @param entry       one entry from computeEffectiveStyleEntries
 */
export function applyEffectiveStyleEntry(node, objectData, theme, entry) {
    const { partName, propName, meta, value, wireKey } = entry
    if (propName === 'radius') {
        if (!node.style(partName).keep_circle) objectData[wireKey] = value
    } else if (meta?.type === 'color') {
        // Explicit background-gradient colors are part of the caller's raw
        // firmware intent, including values equal to a schema/theme default.
        node.addSplitColors(objectData, meta.preserveExplicit ? {} : theme, wireKey, value)
    } else if (meta?.type === 'string') {
        if (value !== '') objectData[wireKey] = value
    } else if (meta?.type === 'resource' && meta.resourceType === 'image') {
        const wireValue = imageSourceToWireValue(value)
        if (wireValue !== '') objectData[wireKey] = wireValue
    } else {
        objectData[wireKey] = value
    }
}
